import {
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type KeyboardCoordinateGetter,
  type SensorDescriptor,
  type SensorOptions,
} from '@dnd-kit/core';

interface DragSensorsOptions {
  /** Сдвиг мыши, с которого начинается перетаскивание; клик — меньше. */
  distance?: number;
  /** Клавиатура: как считать координаты (у сортируемых списков — своя). */
  keyboard?: KeyboardCoordinateGetter | false;
}

/**
 * Один набор датчиков dnd-kit на все перетаскиваемые списки.
 *
 * Мышь и палец — разные датчики, а не общий PointerSensor: тому для пальца
 * нужен `touch-action: none` на элементе, а с ним палец на карточке не
 * может прокрутить колонку — на телефоне доска стояла намертво. Здесь
 * палец начинает тащить только после удержания, короткое касание и
 * движение — обычная прокрутка; элементам достаточно
 * `touch-action: manipulation`.
 */
export function useDragSensors({
  distance = 6,
  keyboard,
}: DragSensorsOptions = {}): SensorDescriptor<SensorOptions>[] {
  const mouse = useSensor(MouseSensor, { activationConstraint: { distance } });
  const touch = useSensor(TouchSensor, {
    // Четверть секунды — как у нативного long press. Допуск в 8px: палец
    // за это время всё равно чуть едет.
    activationConstraint: { delay: 250, tolerance: 8 },
  });
  const keys = useSensor(KeyboardSensor, {
    keyboardCodes: KEYBOARD_CODES,
    ...(keyboard ? { coordinateGetter: keyboard } : {}),
  });

  return useSensors(mouse, touch, keyboard === false ? null : keys);
}

/**
 * Клавиатура берёт элемент пробелом, а не Enter.
 *
 * По умолчанию dnd-kit начинает перетаскивание и с Enter, а Enter на
 * сфокусированном элементе почти везде означает «открыть». Радиксовые меню и
 * диалоги после закрытия возвращают фокус на то, из чего их открыли, — на
 * карточку задачи: нажатый следом Enter вместо открытия поднимал карточку в
 * перетаскивание, и она зависала «плывущей», а доска, пока идёт перенос,
 * живёт черновиком списка — удалённая задача так и оставалась на экране
 * (баг Давида, 23 сентября 2026). Пробел остаётся: он ничего не открывает.
 */
const KEYBOARD_CODES = {
  start: ['Space'],
  cancel: ['Escape'],
  end: ['Space', 'Enter'],
};
